/**
 * DeleteLeaderHandle - Handle for removing leader lines from MLeaders.
 *
 * Appears as a red circle at the arrow tip of each leader line.
 * When clicked (not dragged), removes the leader line.
 * If it's the last leader line, erases the entire MLeader.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import { trackShapeChange } from "@/analytics/drawing-analytics";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, Point3D } from "../types";
import { HandleType, HandlePosition, HANDLE_SIZES } from "../types";
import { AbstractHandle } from "./AbstractHandle";
import { RemoveLeaderLineCommand } from "../../commands/LeaderLineCommand";
import { deleteObj } from "../../core/delete";
import { OpenMode } from "../../core/constants";

/** Red color for delete handles */
const DELETE_LEADER_HANDLE_DRAW_OPTIONS = {
  fillColor: [220, 60, 60] as [number, number, number],
  strokeColor: [255, 255, 255] as [number, number, number],
  radius: HANDLE_SIZES.default * HANDLE_SIZES.handleScale,
};

/**
 * Handle for deleting a leader line from an MLeader.
 */
export class DeleteLeaderHandle extends AbstractHandle {
  readonly type = HandleType.DeleteLeader;
  readonly position = HandlePosition.DeleteLeader;

  /** Entity handle string (stable identifier) */
  protected entityHandle: string;

  /** Leader line index this handle controls */
  private leaderLineIndex: number;

  /** delete handles does not snap to anything */
  snapOptions = { acceptOrtho: false, acceptPolar: false, acceptObject: false };

  /** Arrow tip position (stored for undo) */
  private arrowTip: Point3D;

  /** Start position for detecting click vs drag */
  private startPosition: Point3D | null = null;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityHandle: string,
    leaderLineIndex: number,
    arrowTip: Point3D,
  ) {
    super(viewer, history);
    this.entityHandle = entityHandle;
    this.leaderLineIndex = leaderLineIndex;
    this.arrowTip = arrowTip;
    this.drawOptions = { ...DELETE_LEADER_HANDLE_DRAW_OPTIONS };
    this.worldPosition = { ...arrowTip };
  }

  /**
   * Start - record start position.
   */
  start(context: TransformContext): void {
    if (this.isTransforming) return;
    this.isTransforming = true;
    this.startPosition = { ...context.worldPoint };
  }

  /**
   * Ongoing - no preview needed for delete.
   */
  ongoing(): void {
    // No drag preview needed
  }

  /**
   * End - if click (no significant movement), delete the leader line.
   */
  end(context: TransformContext): void {
    if (!this.isTransforming) return;
    this.isTransforming = false;

    // Check if this was a click (minimal movement) or a drag
    if (this.startPosition) {
      const dx = Math.abs(context.worldPoint.x - this.startPosition.x);
      const dy = Math.abs(context.worldPoint.y - this.startPosition.y);
      const moveDistance = Math.sqrt(dx * dx + dy * dy);

      // If moved more than a small threshold, treat as cancelled drag
      const clickThreshold = 5; // pixels worth of world units at current zoom
      const screenThreshold =
        this.viewer.screenToWorld({ x: clickThreshold, y: 0 }).x -
        this.viewer.screenToWorld({ x: 0, y: 0 }).x;

      if (moveDistance > Math.abs(screenThreshold)) {
        this.startPosition = null;
        return;
      }
    }

    this.startPosition = null;

    // Execute delete command
    trackShapeChange("leader_removed");
    const command = new RemoveLeaderLineCommand(this.viewer, {
      mleaderHandle: this.entityHandle,
      leaderLineIndex: this.leaderLineIndex,
      arrowTip: this.arrowTip,
    });

    this.history.execute(command);

    this.viewer.update();
  }

  /**
   * Cancel - reset state.
   */
  cancel(): void {
    if (!this.isTransforming) return;
    this.isTransforming = false;
    this.startPosition = null;
  }
}

/**
 * Get all arrow tip positions for an MLeader.
 * Returns array of { leaderLineIndex, arrowTip } for each leader line.
 */
export function getMLeaderArrowTips(
  viewer: DRDrawingViewer,
  entityId: DrawingModule.OdDbObjectId,
): Array<{ leaderLineIndex: number; arrowTip: Point3D }> {
  const module = viewer.module;
  const result: Array<{ leaderLineIndex: number; arrowTip: Point3D }> = [];

  const entity = entityId.safeOpenObject(
    OpenMode.kForRead,
    false,
  ) as DrawingModule.OdDbEntity;
  const mleader = module.OdDbMLeader.cast(entity);

  if (!mleader) {
    return result;
  }

  // Get number of leader lines and try each potential index
  // Leader line indexes may not be sequential, so we try a range and check which ones are valid
  const numLines = mleader.numLeaderLines();
  const maxIndexToTry = numLines + 10; // Try extra indexes in case of gaps

  let foundCount = 0;
  for (
    let lineIndex = 0;
    lineIndex < maxIndexToTry && foundCount < numLines;
    lineIndex++
  ) {
    const point = new module.OdGePoint3d(0, 0, 0);
    const res = mleader.getFirstVertex(lineIndex, point);

    if (res === 0) {
      // Valid leader line found
      result.push({
        leaderLineIndex: lineIndex,
        arrowTip: { x: point.x, y: point.y, z: point.z },
      });
      foundCount++;
    }

    deleteObj(point);
  }

  return result;
}
